import { useTranslation } from 'react-i18next';
import {
  IconChevron,
  IconEducation,
  IconLanguages,
  IconSkills,
  IconUser,
} from '../../../../app/components/Icons';
import type {
  StudentProfileStepId,
  StudentProfileStepProgress,
} from '../../application/studentProfileSteps';

const STEP_ICONS = {
  basic: IconUser,
  goals: IconEducation,
  categories: IconSkills,
  languages: IconLanguages,
} satisfies Record<StudentProfileStepId, typeof IconUser>;

type StudentProfileStepsProps = {
  steps: StudentProfileStepProgress[];
  activeStepId: StudentProfileStepId;
  onStepSelect: (id: StudentProfileStepId) => void;
};

export function StudentProfileSteps({
  steps,
  activeStepId,
  onStepSelect,
}: StudentProfileStepsProps) {
  const { t } = useTranslation();

  return (
    <nav className="profile-wrapper__steps" aria-label={t('student-profile.steps_nav')}>
      {steps.map((step) => {
        const active = step.id === activeStepId;
        const Icon = STEP_ICONS[step.id];
        return (
          <button
            key={step.id}
            type="button"
            className={[
              'profile-step',
              active ? 'profile-step--active' : '',
              step.complete ? 'profile-step--complete' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onStepSelect(step.id)}
            aria-current={active ? 'step' : undefined}
          >
            <span className="profile-step__indicator" aria-hidden>
              <span className="profile-step__indicator-dot" />
            </span>
            <span className="profile-step__icon-wrap" aria-hidden>
              <Icon className="profile-step__icon" />
            </span>
            <span className="profile-step__label">
              {t(`student-profile.steps.${step.id}`)}
            </span>
            <span className="profile-step__counter">
              {step.filled}/{step.total}
            </span>
            <span
              className={[
                'profile-step__arrow',
                active ? 'profile-step__arrow--visible' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-hidden
            >
              <IconChevron />
            </span>
          </button>
        );
      })}
    </nav>
  );
}
